import {
  inputBaseClasses,
  ScopedCssBaseline,
  ThemeProvider,
  styled as muiStyled,
} from "@mui/material";

import {
  ManagersLens,
  TABS,
  type IManagersLensProps,
  SendBadgeEvent,
  OpenRequestFeedbackModalEvent,
} from '@workboard/ui-lens';

import { useCallback, useMemo } from "react";
import { GlobalStyle, theme } from "./theme/theme";
import { useTranslation } from "react-i18next";

const StyledCssBaseline = muiStyled(ScopedCssBaseline)({
  backgroundColor: "transparent",

  "body .ui-widget &": {
    // workaround for conflicts with Direct & Dotted page CSS
    "& button": {
      fontSize: 16,
    },
    [`& textarea.${inputBaseClasses.input}`]: {
      "&, &::placeholder": {
        fontSize: 16,
      },
    },
  },
});

export type TManagerLensDrawerProps =
  Pick<IManagersLensProps,
    "userId" | "manager" | "onToast" | "isReadonly" | "hideFixedTrigger" | "staticTriggerElement" | "location" | "hideStaticTrigger" | "title"> & {
      hidePace?: boolean;
    }

export const ManagerLens = ({
  userId,
  manager,
  isReadonly,
  location,
  staticTriggerElement,
  hideFixedTrigger = false,
  hidePace = false,
  onToast = () => null,
  hideStaticTrigger = true,
  title,
}: TManagerLensDrawerProps) => {
  const { t } = useTranslation();
  // eslint-disable-next-line eqeqeq
  const isReadonlyUser = window?.wbConfig?.userLicenseType == '0';
  const isPaceHidden = hidePace ?? window?.wbConfig?.disableMeetingsPaceMeter ?? false;
  const isImpactEnabled = window.wbConfig?.isManagerLensSummaryEnabled ?? false;
  const isFeedbackHelperEnabled = window.wbConfig?.isFeedbackHelperEnabled ?? false;
  const isEnabledNewFeedback = window?.wbConfig?.isEnabledNewFeedback ?? false;
  const isFeedbackDisabledCompletely = window?.wbConfig?.isFeedbackDisabledCompletely ?? false;

  const coauthorLanguage =
    typeof window.wbConfig.coauthorLanguage === 'string'
      ? window.wbConfig.coauthorLanguage
      : 'en';

  let canEdit = !isReadonly ?? false;

  if (isReadonlyUser) {
    canEdit = false;
  }

  const tabs = useMemo<IManagersLensProps["tabs"]>(
    () => [
      isFeedbackHelperEnabled && TABS.feedback,
      isImpactEnabled && TABS.userImpact,
      !isPaceHidden && TABS.pace,
    ],
    [isFeedbackHelperEnabled, isImpactEnabled, isPaceHidden]
  );

  const featureFlags = useMemo<IManagersLensProps["featureFlags"]>(
    () => ({
      isEnabledNewFeedback: !!isEnabledNewFeedback && !isFeedbackDisabledCompletely,
      isFeedbackDisabledCompletely: !!isFeedbackDisabledCompletely,
    }),
    [isEnabledNewFeedback, isFeedbackDisabledCompletely]
  );

  const onSendBadge = useCallback((event: SendBadgeEvent) => {
    const { member_id, analyticsName } = event;
    if (member_id) {
      window.sendBadgeFun(
        null,
        member_id,
        null,
        undefined,
        undefined,
        null,
        undefined,
        analyticsName
      );
    }
  }, []);

  const onOpenRequestFeedbackModal = useCallback(
    (event: OpenRequestFeedbackModalEvent) => {
      const { memberId, analyticsName } = event;
      window.openRequestFeedbackModal({
        memberId,
        analyticName: analyticsName,
      });
    }, []);

  return (
    <StyledCssBaseline>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <ManagersLens
          userId={userId}
          manager={manager}
          staticTriggerElement={staticTriggerElement}
          hideStaticTrigger={hideStaticTrigger}
          isReadonly={!canEdit}
          hideFixedTrigger={hideFixedTrigger}
          location={location}
          onTrackEvent={window.wb_track}
          language={coauthorLanguage}
          onToast={onToast}
          tabs={tabs}
          title={title ?? t('Manager\'s Lens')}
          featureFlags={featureFlags}
          onSendBadge={onSendBadge}
          onOpenRequestFeedbackModal={onOpenRequestFeedbackModal}
        />
      </ThemeProvider>
    </StyledCssBaseline>
  );
};
